import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import {
  Sparkles,
  Users,
  TrendingUp,
  ClipboardList,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Rocket,
  Mail,
  Tag,
} from "lucide-react";

const CATEGORY_TINT = {
  sleep: "bg-indigo-50 text-indigo-700 border-indigo-200",
  metabolic: "bg-amber-50 text-amber-800 border-amber-200",
  hormone: "bg-rose-50 text-rose-700 border-rose-200",
  diagnostics: "bg-blue-50 text-blue-700 border-blue-200",
  wellness: "bg-emerald-50 text-emerald-700 border-emerald-200",
  longevity: "bg-violet-50 text-violet-700 border-violet-200",
  recovery: "bg-orange-50 text-orange-700 border-orange-200",
  uncategorised: "bg-slate-50 text-slate-700 border-slate-200",
};

const WINDOW_OPTIONS = [
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 180, label: "Last 180 days" },
  { days: 365, label: "Last 12 months" },
];

const _fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

export default function PortalScreeningInsights() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clinic/recommendations/insights?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const summary = data?.summary || {};
  const byCategory = data?.by_category || [];
  const unconverted = data?.top_unconverted || [];
  const keywords = data?.top_keywords || [];

  // Pre-compute the top revenue opportunity for the hero callout.
  const topGap = useMemo(
    () =>
      [...byCategory]
        .filter((r) => r.gap > 0)
        .sort((a, b) => b.gap - a.gap)[0] || null,
    [byCategory],
  );

  return (
    <PageShell
      eyebrow="Clinic Intelligence · Screening Insights"
      title="Screening Insights"
      intro="Aggregate signals across every consumer screener your clinic has deployed. Spot gaps where consumers screened but never enrolled — turn screening throughput into program activation."
      testId="screening-insights-page"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="insights-window-select"
            className="text-xs rounded-full border border-slate-200 bg-white px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="insights-refresh"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-3 py-2 text-xs font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      }
    >
      {loading && !data ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-500">
          Aggregating screening data…
        </div>
      ) : (
        <>
          {/* Top-line KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Kpi
              label="Submissions"
              value={summary.submissions || 0}
              accent="bg-blue-500"
              testid="insights-kpi-submissions"
              icon={ClipboardList}
            />
            <Kpi
              label="Unique consumers"
              value={summary.unique_consumers || 0}
              accent="bg-violet-500"
              testid="insights-kpi-unique"
              icon={Users}
            />
            <Kpi
              label="Enrolled"
              value={summary.enrolled_count || 0}
              accent="bg-emerald-500"
              testid="insights-kpi-enrolled"
              icon={Rocket}
            />
            <Kpi
              label="Conversion rate"
              value={`${summary.conversion_rate_pct || 0}%`}
              accent="bg-amber-500"
              testid="insights-kpi-conversion"
              icon={TrendingUp}
            />
          </div>

          {/* Hero opportunity callout — single most actionable gap */}
          {topGap && (
            <div
              data-testid="insights-top-gap-callout"
              className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 mb-5"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-800" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-800">
                    Highest-impact opportunity
                  </div>
                  <div className="font-display text-lg font-bold text-slate-900 leading-snug mt-0.5">
                    {topGap.gap} consumer{topGap.gap === 1 ? "" : "s"} screened for{" "}
                    <span className="capitalize">{topGap.category}</span> but haven't been enrolled.
                  </div>
                  <div className="text-[12px] text-slate-700 mt-1.5">
                    {topGap.active_program_count > 0 ? (
                      <>
                        You already run{" "}
                        <b>{topGap.active_program_count} active {topGap.category} program{topGap.active_program_count === 1 ? "" : "s"}</b>
                        {topGap.top_program && (
                          <> · most popular: <b>{topGap.top_program.name}</b></>
                        )}
                        . That's a clear path to enrollment.
                      </>
                    ) : (
                      <>No active <b>{topGap.category}</b> programs in your library yet — clinics typically convert this category at 30-50% once a program is live.</>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {topGap.top_program ? (
                      <Link
                        to={`/portal/programs/${topGap.top_program.id}`}
                        data-testid="insights-top-gap-cta"
                        className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-800"
                      >
                        <Rocket className="w-3.5 h-3.5" />
                        Manage {topGap.top_program.name}
                      </Link>
                    ) : (
                      <Link
                        to="/portal/programs"
                        data-testid="insights-top-gap-cta"
                        className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-slate-800"
                      >
                        <Rocket className="w-3.5 h-3.5" />
                        Build a {topGap.category} program
                      </Link>
                    )}
                    <Link
                      to="/portal/order-services"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
                    >
                      Browse the catalog <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Per-category opportunity table */}
          <Section title="By category" testid="insights-by-category" className="mb-5">
            {byCategory.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-8 text-center text-sm text-slate-500">
                No screening submissions in this window. Once consumers complete a screener, opportunities will surface here.
              </div>
            ) : (
              <div
                className="rounded-2xl border border-slate-200 bg-white overflow-x-auto"
                data-testid="insights-category-table"
              >
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5">Category</th>
                      <th className="text-right px-4 py-2.5">Submissions</th>
                      <th className="text-right px-4 py-2.5">Unique</th>
                      <th className="text-right px-4 py-2.5">Enrolled</th>
                      <th className="text-right px-4 py-2.5">Gap</th>
                      <th className="text-right px-4 py-2.5">Conv. rate</th>
                      <th className="text-left px-4 py-2.5">Coverage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {byCategory.map((r) => (
                      <tr
                        key={r.category}
                        data-testid={`insights-category-row-${r.category}`}
                        className="hover:bg-slate-50/60"
                      >
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase font-bold ${CATEGORY_TINT[r.category] || CATEGORY_TINT.uncategorised}`}
                          >
                            {r.category}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">
                          {r.submissions}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                          {r.unique_consumers}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700 font-semibold">
                          {r.enrolled_consumers}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          <span
                            className={`inline-flex items-center gap-1 font-bold ${
                              r.gap === 0 ? "text-slate-400" : "text-amber-700"
                            }`}
                          >
                            {r.gap}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          <span
                            className={`font-semibold ${
                              r.conversion_rate_pct >= 50
                                ? "text-emerald-700"
                                : r.conversion_rate_pct >= 25
                                  ? "text-amber-700"
                                  : "text-rose-700"
                            }`}
                          >
                            {r.conversion_rate_pct}%
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-[12px] text-slate-700">
                          {r.active_program_count > 0 ? (
                            <span>
                              {r.active_program_count} active prog
                              {r.active_program_count === 1 ? "" : "s"}
                              {r.top_program && (
                                <Link
                                  to={`/portal/programs/${r.top_program.id}`}
                                  className="ml-1.5 text-blue-700 hover:underline font-semibold"
                                >
                                  · {r.top_program.name}
                                </Link>
                              )}
                            </span>
                          ) : (
                            <Link
                              to="/portal/programs"
                              data-testid={`insights-build-${r.category}`}
                              className="text-amber-800 hover:underline font-semibold inline-flex items-center gap-1"
                            >
                              <Rocket className="w-3 h-3" /> Build one
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Two-column footer: unconverted consumers + keyword signals */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Section title="Unconverted consumers" testid="insights-unconverted" className="">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> Top 10 — most recent screeners without enrollment
                </div>
                {unconverted.length === 0 ? (
                  <div className="text-[12px] text-slate-500 py-3">
                    Every consumer who screened in this window is already enrolled. Excellent conversion.
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {unconverted.map((u, i) => (
                      <li
                        key={`${u.consumer_email}-${i}`}
                        data-testid={`insights-unconverted-row-${i}`}
                        className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 text-sm truncate">
                            {u.consumer_name || u.consumer_email || "—"}
                          </div>
                          <div className="text-[10px] text-slate-500 inline-flex items-center gap-2">
                            <span>{_fmtDate(u.submission_at)}</span>
                            {u.category && (
                              <span
                                className={`rounded-full px-1.5 py-0.5 text-[9px] uppercase font-bold ${CATEGORY_TINT[u.category] || CATEGORY_TINT.uncategorised}`}
                              >
                                {u.category}
                              </span>
                            )}
                          </div>
                        </div>
                        {u.consumer_email && (
                          <a
                            href={`mailto:${u.consumer_email}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                            title="Email this consumer"
                          >
                            <Mail className="w-3 h-3" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Section>

            <Section title="Common screening signals" testid="insights-keywords" className="">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Top words in consumer answers
                </div>
                {keywords.length === 0 ? (
                  <div className="text-[12px] text-slate-500 py-3">
                    Not enough text-type answers in this window to surface trends.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {keywords.map((k) => (
                      <span
                        key={k.word}
                        data-testid={`insights-keyword-${k.word}`}
                        className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      >
                        {k.word}
                        <span className="bg-slate-900 text-white rounded-full text-[9px] min-w-[1.1rem] h-4 px-1 inline-flex items-center justify-center font-bold">
                          {k.count}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mt-3 leading-snug">
                  Surfaced from consumer free-text answers — directional only, not clinical.
                </div>
              </div>
            </Section>
          </div>
        </>
      )}
    </PageShell>
  );
}

function Section({ title, testid, className = "", children }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-1.5 h-6 rounded-full ${accent}`} />
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
        {label}
      </div>
      <div className="font-display text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
